import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, UserCheck, CreditCard, Clock,
  Search, Plus, Eye, SquarePen, Ban, RotateCcw,
  ChevronLeft, ChevronRight, AlertTriangle, RefreshCw
} from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import RowActions from '../components/RowActions';
import EstadoDialog from '../components/EstadoDialog';
import { useToast } from '../components/ToastProvider';
import { clientesAPI, describirError } from '../services/api';
import { formatoTelefono } from '../utils/finanzas';
import '../styles/Pages.css';
import '../styles/DataTable.css';

const LIMITES = [25, 50, 100];

const iniciales = (nombre = '') =>
  nombre.trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '?';

function Clientes() {
  const navigate = useNavigate();
  const toast = useToast();

  const [datos, setDatos] = useState({ data: [], pagination: null, metrics: null });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [busqueda, setBusqueda] = useState('');
  const [busquedaAplicada, setBusquedaAplicada] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroModalidad, setFiltroModalidad] = useState('');
  const [filtroFrecuencia, setFiltroFrecuencia] = useState('');
  const [sortBy, setSortBy] = useState('nombre_asc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [dialogo, setDialogo] = useState(null);
  const [aplicandoEstado, setAplicandoEstado] = useState(false);

  // Debounce de 300 ms: se escribe libremente y solo se consulta cuando
  // la escritura se detiene.
  const primeraCarga = useRef(true);
  useEffect(() => {
    const t = setTimeout(() => {
      setBusquedaAplicada(busqueda.trim());
      if (!primeraCarga.current) setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [busqueda]);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const params = { page, limit, sortBy };
      if (busquedaAplicada) params.q = busquedaAplicada;
      if (filtroEstado) params.estado = filtroEstado;
      if (filtroModalidad) params.modalidad = filtroModalidad;
      if (filtroFrecuencia) params.frecuencia = filtroFrecuencia;

      const { data } = await clientesAPI.listar(params);
      setDatos(data);
    } catch (e) {
      // Sin datos de respaldo inventados: si la API falla, se dice.
      setError(describirError(e));
      setDatos({ data: [], pagination: null, metrics: null });
    } finally {
      setCargando(false);
      primeraCarga.current = false;
    }
  }, [page, limit, busquedaAplicada, filtroEstado, filtroModalidad, filtroFrecuencia, sortBy]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const aplicarEstado = async (motivo) => {
    setAplicandoEstado(true);
    try {
      await clientesAPI.cambiarEstado(dialogo.cliente.id, dialogo.destino, motivo);
      toast.exito(
        dialogo.destino === 'BLOQUEADO' ? 'Cliente bloqueado.' : 'Cliente reactivado.'
      );
      setDialogo(null);
      cargar();
    } catch (e) {
      toast.error(describirError(e).mensaje);
    } finally {
      setAplicandoEstado(false);
    }
  };

  const acciones = (c) => [
    { label: 'Ver expediente', icon: Eye, onClick: () => navigate(`/clientes/${c.id}`) },
    { label: 'Editar', icon: SquarePen, onClick: () => navigate(`/clientes/${c.id}/editar`) },
    c.estado === 'BLOQUEADO'
      ? { label: 'Reactivar', icon: RotateCcw, onClick: () => setDialogo({ cliente: c, destino: 'ACTIVO' }) }
      : { label: 'Bloquear', icon: Ban, danger: true, onClick: () => setDialogo({ cliente: c, destino: 'BLOQUEADO' }) }
  ];

  const m = datos.metrics;
  const kpis = [
    { label: 'Clientes registrados', value: m ? (m.registrados ?? m.total) : '—', icon: Users },
    { label: 'Clientes con saldo', value: m ? m.conSaldo : '—', icon: CreditCard },
    { label: 'Clientes sin saldo actual', value: m ? m.sinSaldo : '—', icon: UserCheck },
    { label: 'Clientes al corriente', value: m ? (m.alCorriente ?? m.cartera0_30) : '—', icon: Clock, brand: true }
  ];

  const p = datos.pagination;
  const hayFiltros = busquedaAplicada || filtroEstado || filtroModalidad || filtroFrecuencia || sortBy !== 'nombre_asc';

  return (
    <div className="page-container">
      <section className="kpi-grid" aria-label="Resumen de clientes">
        {kpis.map(({ label, value, icon: Icon, brand }) => (
          <article key={label} className={`kpi-card ${brand ? 'is-brand' : ''}`}>
            <span className="kpi-icon">
              <Icon size={17} strokeWidth={1.75} />
            </span>
            <p className="kpi-label">{label}</p>
            <p className="kpi-value">{value}</p>
          </article>
        ))}
      </section>

      <div className="datatable-container has-cards">
        <div className="datatable-toolbar">
          <div className="datatable-search">
            <Search size={16} strokeWidth={1.75} className="datatable-search-icon" />
            <input
              type="search"
              placeholder="Buscar cliente..."
              aria-label="Buscar clientes"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>

          <div className="datatable-filters">
            <select
              aria-label="Filtrar por estado"
              className="filtro-select"
              value={filtroEstado}
              onChange={(e) => { setFiltroEstado(e.target.value); setPage(1); }}
            >
              <option value="">Todos los estados</option>
              <option value="ACTIVO">Activo</option>
              <option value="INACTIVO">Inactivo</option>
              <option value="BLOQUEADO">Bloqueado</option>
            </select>

            <select
              aria-label="Filtrar por modalidad"
              className="filtro-select"
              value={filtroModalidad}
              onChange={(e) => { setFiltroModalidad(e.target.value); setPage(1); }}
            >
              <option value="">Todas las modalidades</option>
              <option value="PERSONAL">Personal</option>
              <option value="GRUPAL">Grupal</option>
              <option value="COLOCADORA">Colocadora</option>
            </select>

            <select
              aria-label="Filtrar por frecuencia"
              className="filtro-select"
              value={filtroFrecuencia}
              onChange={(e) => { setFiltroFrecuencia(e.target.value); setPage(1); }}
            >
              <option value="">Todas las frecuencias</option>
              <option value="SEMANAL">Semanal</option>
              <option value="QUINCENAL">Quincenal</option>
            </select>

            <select
              aria-label="Ordenar por"
              className="filtro-select"
              value={sortBy}
              onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
            >
              <option value="nombre_asc">Nombre (A–Z)</option>
              <option value="nombre_desc">Nombre (Z–A)</option>
              <option value="folio_asc">Folio ascendente</option>
              <option value="folio_desc">Folio descendente</option>
            </select>
          </div>

          <button className="btn btn-primary datatable-add" onClick={() => navigate('/clientes/nuevo')}>
            <Plus size={16} strokeWidth={2} />
            Nuevo cliente
          </button>
        </div>

        {filtroModalidad && (
          <div className="datatable-hint-bar">
            <span className="datatable-hint-text">
              * Un cliente puede pertenecer a más de una modalidad.
            </span>
          </div>
        )}

        {error ? (
          <div className="estado-vacio">
            <span className="estado-vacio-icono is-error">
              <AlertTriangle size={20} strokeWidth={1.75} />
            </span>
            <p className="estado-vacio-titulo">No fue posible cargar clientes.</p>
            <p className="estado-vacio-texto">{error.mensaje}</p>
            <button className="btn btn-secondary" onClick={cargar}>
              <RefreshCw size={15} strokeWidth={1.75} />
              Reintentar
            </button>
          </div>
        ) : cargando ? (
          <div className="estado-vacio">
            <p className="estado-vacio-texto">Cargando clientes…</p>
          </div>
        ) : datos.data.length === 0 ? (
          <div className="estado-vacio">
            <span className="estado-vacio-icono">
              <Users size={20} strokeWidth={1.75} />
            </span>
            <p className="estado-vacio-titulo">
              {hayFiltros ? 'Ningún cliente coincide con la búsqueda.' : 'Todavía no hay clientes.'}
            </p>
            {!hayFiltros && (
              <button className="btn btn-primary" onClick={() => navigate('/clientes/nuevo')}>
                <Plus size={16} strokeWidth={2} />
                Crear el primero
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="datatable-wrapper">
              <table className="datatable">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>RFC</th>
                    <th>Teléfono</th>
                    <th>Ciudad / Municipio</th>
                    <th>Estado</th>
                    <th className="th-actions">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.data.map((c) => (
                    <tr key={c.id} onClick={() => navigate(`/clientes/${c.id}`)} className="fila-clicable">
                      <td>
                        <div className="cell-identity">
                          <span className="avatar">{iniciales(c.nombreCompleto)}</span>
                          <span className="cell-identity-text">
                            <span className="cell-primary">{c.nombreCompleto}</span>
                            <span className="cell-secondary">{c.folio}</span>
                          </span>
                        </div>
                      </td>
                      <td>{c.rfc || <span className="cell-muted">—</span>}</td>
                      <td>{formatoTelefono(c.telefonoPrincipal)}</td>
                      <td>{c.domicilio.municipio || <span className="cell-muted">—</span>}</td>
                      <td><StatusBadge value={c.estado} /></td>
                      <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                        <RowActions actions={acciones(c)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="datatable-cards">
              {datos.data.map((c) => (
                <article key={c.id} className="record-card">
                  <div className="record-card-body" onClick={() => navigate(`/clientes/${c.id}`)}>
                    <div className="cell-identity">
                      <span className="avatar">{iniciales(c.nombreCompleto)}</span>
                      <span className="cell-identity-text">
                        <span className="cell-primary">{c.nombreCompleto}</span>
                        <span className="cell-secondary">{c.folio}</span>
                      </span>
                    </div>
                    <dl className="record-card-meta">
                      <div>
                        <dt>Teléfono</dt>
                        <dd>{formatoTelefono(c.telefonoPrincipal)}</dd>
                      </div>
                      <div>
                        <dt>RFC</dt>
                        <dd>{c.rfc || '—'}</dd>
                      </div>
                      <div>
                        <dt>Estado</dt>
                        <dd><StatusBadge value={c.estado} /></dd>
                      </div>
                    </dl>
                  </div>
                  <div className="record-card-actions">
                    <RowActions actions={acciones(c)} />
                  </div>
                </article>
              ))}
            </div>

            {p && (
              <div className="paginacion">
                <p className="paginacion-info">
                  {(p.page - 1) * p.limit + 1}–{Math.min(p.page * p.limit, p.total)} de {p.total}
                </p>

                <div className="paginacion-controles">
                  <select
                    aria-label="Registros por página"
                    className="filtro-select"
                    value={limit}
                    onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                  >
                    {LIMITES.map((l) => (
                      <option key={l} value={l}>{l} por página</option>
                    ))}
                  </select>

                  <button
                    className="btn btn-icon"
                    onClick={() => setPage((x) => Math.max(1, x - 1))}
                    disabled={p.page <= 1}
                    aria-label="Página anterior"
                  >
                    <ChevronLeft size={16} strokeWidth={2} />
                  </button>
                  <span className="paginacion-pagina">{p.page} / {p.totalPages}</span>
                  <button
                    className="btn btn-icon"
                    onClick={() => setPage((x) => Math.min(p.totalPages, x + 1))}
                    disabled={p.page >= p.totalPages}
                    aria-label="Página siguiente"
                  >
                    <ChevronRight size={16} strokeWidth={2} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {dialogo && (
        <EstadoDialog
          cliente={dialogo.cliente}
          estadoDestino={dialogo.destino}
          enviando={aplicandoEstado}
          onConfirmar={aplicarEstado}
          onCerrar={() => setDialogo(null)}
        />
      )}
    </div>
  );
}

export default Clientes;
